import { AppErrorCard } from '@components/error-card/app-error-card';
import { ScreenReaderElement } from '@components/screen-reader/screen-reader-element';
import { StandardButton } from '@eika/button';
import { Skeleton } from '@eika/skeleton';
import type { RequestException } from '@eika-infrastruktur/request';
import { useSession } from '@react-query/hooks/query/use-session';
import { useTranslation } from '@translations';
import { GuaranteedSessionContext } from './session-context';

interface GuaranteedSessionContextProviderProps {
    children: React.ReactNode;
    fallback?: React.ReactNode;
    errorFallback?: ({ error }: ErrorFallbackProps) => React.ReactNode;
}

export const GuaranteedSessionContextProvider = ({
    children,
    fallback = <LoadingFallback />,
    errorFallback = ErrorFallback,
}: GuaranteedSessionContextProviderProps) => {
    const { data: session, isPending, error } = useSession();

    if (isPending) {
        return <>{fallback}</>;
    }

    if (error) {
        return <>{errorFallback({ error })}</>;
    }

    return <GuaranteedSessionContext.Provider value={{ session }}>{children}</GuaranteedSessionContext.Provider>;
};

const LoadingFallback = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Skeleton height={40} />
        <Skeleton height={40} width="50%" />
    </div>
);

interface ErrorFallbackProps {
    error: RequestException;
}

const ErrorFallback = ({ error }: ErrorFallbackProps) => {
    const { errors, tryAgain } = useTranslation();
    const { refetch } = useSession();

    return (
        <AppErrorCard description={errors.defaultMessage} exception={error}>
            <ScreenReaderElement text={errors.defaultMessage} role="alert" />
            <StandardButton
                onClick={() => {
                    refetch();
                }}
            >
                {tryAgain}
            </StandardButton>
        </AppErrorCard>
    );
};
